/**
 * EmergencyStopButton - Tlačítko pro nouzové zastavení všech zařízení
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { OctagonAlert, TriangleAlert } from 'lucide-react';

interface EmergencyStopButtonProps {
  onEmergencyStop: () => Promise<void>;
  onEmergencyReset?: () => Promise<void>;
  isEmergencyStopped?: boolean;
  activeCount: number;
  variant?: 'full' | 'compact' | 'icon';
  disabled?: boolean;
}

export const EmergencyStopButton: React.FC<EmergencyStopButtonProps> = ({
  onEmergencyStop,
  onEmergencyReset,
  isEmergencyStopped = false,
  activeCount,
  variant = 'full',
  disabled = false,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleClick = () => {
    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsExecuting(true);
    try {
      if (isEmergencyStopped && onEmergencyReset) {
        await onEmergencyReset();
      } else {
        await onEmergencyStop();
      }
    } finally {
      setIsExecuting(false);
      setIsDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const buttonContent = (
    <>
      <OctagonAlert className={variant === 'icon' ? 'w-7 h-7' : 'w-5 h-5'} />
      {variant !== 'icon' && (
        <div className="ml-2 flex flex-col items-start">
          <span
            className={`font-bold leading-tight ${variant === 'compact' ? 'text-sm' : 'text-base'}`}
          >
            {isEmergencyStopped ? 'ZRUŠIT STOP' : 'CENTRAL STOP'}
          </span>
          {variant === 'full' && (
            <span className="text-xs opacity-80 font-normal">
              {isEmergencyStopped ? 'Obnovit provoz' : `${activeCount} aktivních`}
            </span>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <button
          onClick={handleClick}
          disabled={disabled}
          aria-label={isEmergencyStopped ? 'Zrušit nouzové zastavení' : 'Nouzové zastavení všech zařízení'}
          className={`
            relative overflow-hidden transition-all duration-300
            ${isEmergencyStopped 
              ? 'bg-gradient-to-br from-green-500 to-green-700 hover:from-green-400 hover:to-green-600 shadow-green-500/40 hover:shadow-green-500/50' 
              : 'bg-gradient-to-br from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 shadow-red-500/40 hover:shadow-red-500/50'}
            text-white shadow-lg
            flex items-center justify-center
            rounded-xl
            focus-visible:ring-4 ${isEmergencyStopped ? 'focus-visible:ring-green-500/50' : 'focus-visible:ring-red-500/50'} focus-visible:outline-none
            ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : ''}
            ${variant === 'icon' ? 'p-2 min-w-[48px]' : variant === 'compact' ? 'py-2 px-4' : 'py-3 px-6'}
          `}
        >
          {buttonContent}
        </button>
      </motion.div>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {isDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCancel}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Dialog Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="stop-dialog-title"
              className="relative bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-red-500/20"
            >
              <div className="p-6">
                <div className={`flex items-center gap-2 mb-4 ${isEmergencyStopped ? 'text-green-500' : 'text-red-500'}`}>
                  <TriangleAlert className="w-6 h-6" />
                  <h2 id="stop-dialog-title" className="text-xl font-bold">
                    {isEmergencyStopped ? 'Potvrzení zrušení nouzového stavu' : 'Potvrzení nouzového zastavení'}
                  </h2>
                </div>

                <p className="mb-4 text-gray-700 dark:text-zinc-300">
                  {isEmergencyStopped 
                    ? <span>Opravdu chcete <strong>zrušit nouzový stav</strong>?</span>
                    : <span>Opravdu chcete <strong>vypnout všechna zařízení</strong>?</span>}
                </p>

                <div className={`p-4 rounded-xl border mb-6 ${
                  isEmergencyStopped 
                    ? 'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30' 
                    : 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30'
                }`}>
                  <p className="text-sm text-gray-500 dark:text-zinc-400 mb-1">
                    {isEmergencyStopped ? 'Tato akce způsobí:' : 'Tato akce okamžitě vypne:'}
                  </p>
                  <h3 className={`text-2xl font-bold my-1 ${isEmergencyStopped ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {isEmergencyStopped ? 'Obnovení provozu' : `${activeCount} aktivních zařízení`}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-zinc-400">
                    {isEmergencyStopped 
                      ? 'Opět bude možné zapínat a vypínat čerpadla, ventily a další aktuátory.'
                      : 'Všechna čerpadla, ventily, topení a další zařízení budou okamžitě vypnuty.'}
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancel}
                    disabled={isExecuting}
                    className="px-4 py-2 rounded-lg text-gray-600 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors font-medium border border-transparent hover:border-gray-200 dark:hover:border-zinc-700"
                  >
                    Zrušit
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isExecuting}
                    className={`px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 transition-colors shadow-lg ${
                      isEmergencyStopped 
                        ? 'bg-green-600 hover:bg-green-700 shadow-green-500/20' 
                        : 'bg-red-600 hover:bg-red-700 shadow-red-500/20'
                    }`}
                  >
                    {isExecuting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                        <span>Zpracovávám...</span>
                      </>
                    ) : (
                      <span>{isEmergencyStopped ? 'Potvrdit zrušení' : 'Potvrdit zastavení'}</span>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EmergencyStopButton;
